import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState
} from '@microsoft/signalr';
import { useAuth } from 'react-oidc-context';
import { useLocalStorage } from 'usehooks-ts';
import { merge, isEmpty, isEqual, pick, cloneDeep } from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import {
  GetGameStateHub,
  getGameState,
  updateGameSettings,
  getARMiniGamesResult,
  GetFacilitatorGameStateHub,
  postBroadcastGameStateAsync
} from '../services/games';
import {
  UpdateGameStateAsync,
  UpdatePlayerStateAsync
} from '../utils/game-engine/base';
import {
  getTaskImage,
  UpdateTaskAsync,
  GetTaskById as GetPlayerTaskById
} from '../utils/game-engine/assets';
import { PlayerContext } from './player';
import { GameState, GameStatus } from '../types/state';
import { uuid } from '../types/common-helper';
import { putToStore, getFromStore } from '../utils/indexedDB';
import { ArMiniGames } from '../constants/argame';
import { useGameEvent } from '../hooks/use-game-event';
import { ManualScoring } from '../types/manual-scoring';
import { MapContext } from '../components/map/map-context';
import { MapEntity } from '../types/game-document/entities';
import { addManualScoringAsync } from '../services/players';
import { useGame } from '../hooks/use-game';
import { useGameDocument } from '../hooks/use-game-document';
import { HubResponse } from '../types/responses/hub-response';
import { InGameSettings } from '../types/game-document/settings';
import { DefaultInGame } from '../types/game-document/settings/in-game';
import { validateSettings } from '../utils/game-document/validate-setting';
import { TaskContent } from '../types/game-document/entities/task-content';
import { GetTaskById as GetTaskGameById } from '../utils/game-document/assets';
import { GetTaskContentById } from '../utils/game-document/assets/task-contents';
import { MapIllustrationContext } from '../components/map/map-illustration-context';

export interface TaskScoring {
  taskId: string;
  formId: string;
  playerCode: string;
}

const GameContext = React.createContext<
  [
    initialState: GameContextState,
    setState: React.Dispatch<React.SetStateAction<GameContextState>>,
    HubConnection | null,
    isOnline: boolean
  ]
>([{}, () => {}, null, true]);

interface GameStateProviderProps {
  gameCode: string;
  isFromFacilitator?: boolean;
  children: React.ReactNode;
}

const GameStateProvider = ({
  gameCode,
  isFromFacilitator,
  ...props
}: GameStateProviderProps) => {
  const auth = useAuth();
  const game = useGame(gameCode, isFromFacilitator);
  const gameDocument = useGameDocument(gameCode, isFromFacilitator);
  const [player, setPlayer] = useContext(PlayerContext);

  useEffect(() => {
    const gameStatus = game.data?.gameStatus;
    const actualStartDateUtc = game.data?.actualStartDateUtc;
    if (gameStatus) {
      setState((prev) => {
        const hasStartedUtc = prev.gameState?.startedUtc;
        const newStartedUtc =
          gameStatus === 'PreGame'
            ? undefined
            : (hasStartedUtc ?? (actualStartDateUtc ? new Date(actualStartDateUtc) : undefined));

        if (
          prev.gameState?.status === gameStatus &&
          prev.gameState?.startedUtc === newStartedUtc
        )
          return prev;

        return {
          ...prev,
          gameState: {
            ...prev.gameState!,
            status: gameStatus as GameStatus,
            startedUtc: newStartedUtc
          }
        };
      });
    }
  }, [game.data?.gameStatus, game.data?.actualStartDateUtc]);

  const [, setTaskScorings] = useLocalStorage<ManualScoring[]>(
    `task-scorings-${gameCode}`,
    []
  );
  const [isOnline, setIsOnline] = useState(true);

  const runtimeSetting = useMemo(() => {
    const DefaultRuntime = {
      runtime: { inGame: gameDocument.data?.settings.inGame ?? DefaultInGame }
    };
    if (gameDocument.data) {
      if (!gameDocument.data.settings.inGame) return DefaultRuntime;

      const defaultSetting = Object.keys(DefaultRuntime).sort();
      const currentSetting = Object.keys({
        runtime: { inGame: gameDocument.data.settings.inGame }
      }).sort();

      if (
        !isEqual(defaultSetting, currentSetting) ||
        !validateSettings(DefaultRuntime, {
          runtime: { inGame: gameDocument.data.settings.inGame }
        })
      ) {
        return DefaultRuntime;
      }

      return { runtime: { inGame: gameDocument.data.settings.inGame } };
    }

    return DefaultRuntime;
  }, [gameDocument.data]);

  const [state, setState] = useState<GameContextState>({
    gameCode,
    gameState: { ...BuildNewGameState(gameCode, runtimeSetting.runtime) }
  });

  useEffect(() => {
    let isCanceled = false;

    const loadCachedState = async () => {
      try {
        const cached = await getFromStore<GameContextState>(
          'game-state',
          gameCode
        );
        if (isCanceled) return;

        if (cached && cached.gameCode === gameCode && cached.gameState) {
          setState((prev) => {
            const acceptFields = ['isPreloadCompleted', 'totalAssets'];
            const newGameStatus = pick(prev.gameState, acceptFields);
            const updatedGameState: GameState = merge(
              { ...prev.gameState },
              cached.gameState,
              newGameStatus
            ) as GameState;
            return {
              ...prev,
              ...cached,
              gameState: updatedGameState
            };
          });
        }
      } catch (err) {
        console.error('Failed to load cached game state from IndexedDB:', err);
      }
    };

    if (gameCode) {
      loadCachedState();
    }

    return () => {
      isCanceled = true;
    };
  }, [gameCode]);

  const [gameStateConnection, setGameStateConnection] =
    useState<HubConnection | null>(null);

  const updateGameState = (gameState: GameState): void => {
    // when cloning the game state object from server, have to ignore 2 fields as it is only used locally
    const acceptFields = ['isPreloadCompleted', 'totalAssets'];

    setState((prev) => {
      // pick acceptFields with value, then merge into gamestate from server
      const newGameStatus = pick(prev.gameState, acceptFields);

      const updatedGameState: GameState = merge(
        { ...prev.gameState, ...gameState },
        newGameStatus
      );
      return UpdateGameStateAsync(prev, updatedGameState);
    });
  };

  const GameFacilitatorContentHandler = async (contentCode: string) => {
    setState((prev) => {
      const updatedGameState: GameContextState = {
        ...prev,
        contentId: contentCode
      };
      return UpdateGameStateAsync(
        updatedGameState,
        updatedGameState.gameState!
      );
    });
  };

  const map = React.useContext(MapContext);
  const mapIllustration = React.useContext(MapIllustrationContext);

  const mapContext = React.useMemo(() => {
    const location = player.playerState?.location;
    let mapEntity: MapEntity | undefined = undefined;

    if (location === 'world-map' || location === '') {
      const mapAsId = gameDocument.data?.rules.worldMap.mapAssId;
      mapEntity = gameDocument.data?.assets.maps?.find((m) => m.id === mapAsId);
    } else {
      const zone = gameDocument.data?.rules.worldMap.zones.find(
        (m) => m.zoneAssId === location
      );
      mapEntity = gameDocument.data?.assets.maps?.find(
        (m) => m.id === zone?.mapAssId
      );
    }

    return mapEntity?.type === 'illustrated' ? mapIllustration : map;
  }, [player.playerState?.location, gameDocument.data, map, mapIllustration]);

  const { executeTaskEvent } = useGameEvent();

  const playerRef = React.useRef<typeof player | null>(null);
  playerRef.current = player;

  const updateARGames = async (taskId: string, data: any) => {
    const prevPlayer = playerRef.current;
    if (!prevPlayer) return;

    let updatedPlayer = cloneDeep(prevPlayer);
    const task = GetPlayerTaskById(updatedPlayer, taskId);
    if (!task || task.status === 'completed') return;

    task.status = 'completed';
    task.dataARMiniGame = data;

    const taskGame = GetTaskGameById(gameDocument.data, taskId);

    if (taskGame) {
      if (taskGame.hideWhenComplete) {
        const getTask = mapContext.getOverlayById(taskId);
        if (getTask) mapContext.removeOverlay(getTask);
      } else {
        const imgUrl = getTaskImage(
          gameDocument.data!,
          'completed',
          taskGame,
          player?.playerState?.language?.name
        );

        const taskIcon = document.getElementById(
          `icon-${taskId}`
        ) as HTMLImageElement;
        if (taskIcon) taskIcon.src = imgUrl;
      }
    }

    const response = UpdateTaskAsync(updatedPlayer, taskId, task);

    const scoring = async (taskContent: TaskContent) => {
      if (
        taskContent.ARMiniGame &&
        taskContent.ARMiniGameSetting &&
        !taskContent.ARMiniGameSetting.manualScoring
      ) {
        const miniGameType = taskContent.ARMiniGame;
        const contentScore =
          taskContent.ARMiniGameSetting.score ??
          ArMiniGames[miniGameType].defaultScore;
        let finalScore = contentScore;

        if (
          ['Game Cube', 'Summer Suitcase', 'The Portal'].includes(miniGameType)
        ) {
          finalScore = data.passed ? finalScore : 0;
        }

        if (['Sound Waves'].includes(miniGameType)) {
          // percentage times maks score to get final score
          data.correctAnswerRate = data.correctAnswerRate.toFixed(0);
          finalScore = (data.correctAnswerRate / 100) * finalScore;
        }

        if (
          [
            'Where in the World?',
            'Where in the World? - Easy',
            'Where in the World? - Hard'
          ].includes(miniGameType)
        ) {
          // percentage times maks score to get final score
          data.correctAnswerRate = data.correctAnwerRate.toFixed(0);
          finalScore = (data.correctAnwerRate / 100) * finalScore;
        }

        task.dataARMiniGame.score = finalScore;
        response.score = (response.score ?? 0) + finalScore;
      } else {
        await addManualScoringAsync(
          gameCode,
          response.code,
          taskId,
          taskId,
          JSON.stringify(data)
        );
      }
    };

    if (taskGame) {
      const index = response.openedTask?.findIndex((t) => t === taskId);
      if (typeof index === 'number' && index !== -1) {
        response.openedTask = [];
        response.latestCompletedTaskId = taskId;
      }

      const selectedTaskContent = GetTaskContentById(
        gameDocument.data,
        taskGame.taskContentId ?? ''
      );
      if (selectedTaskContent && selectedTaskContent.ARMiniGameSetting)
        await scoring(selectedTaskContent);
    } else {
      response.openedOnDemandTask = '';

      const selectedTaskContent = GetTaskContentById(gameDocument.data, taskId);
      if (selectedTaskContent && selectedTaskContent.ARMiniGameSetting)
        await scoring(selectedTaskContent);
    }

    updatedPlayer = UpdatePlayerStateAsync(updatedPlayer, response);
    if (taskGame) {
      updatedPlayer = await executeTaskEvent(
        taskId,
        ['onCompleteTask', 'onCloseTask'],
        updatedPlayer
      );
    }

    setPlayer(updatedPlayer);
  };

  const getARGameResult = async () => {
    try {
      const result = await getARMiniGamesResult(
        gameCode,
        player.playerState?.code ?? ''
      );

      if (result) {
        const { data } = result;
        updateARGames(data.taskId, data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const GameConnectionListener = async (connection: HubConnection) => {
    connection.on(`StateUpdated`, (gameState: GameState) => {
      if (!isEmpty(gameState) && gameState.code === gameCode) {
        try {
          updateGameState(gameState);
        } catch (error) {
          console.error(error);
        }
      }
    });

    connection.on(`FacilitatorContentAdded`, (contentCode: string) => {
      if (!isEmpty(contentCode)) {
        try {
          GameFacilitatorContentHandler(contentCode);
        } catch (error) {
          console.error(error);
        }
      }
    });

    connection.on(`AnswerSubmitted`, (data: TaskScoring) => {
      setTaskScorings((prev) => {
        const key = `${data.playerCode}-${data.taskId}-${data.formId}`;

        const existingKeys = new Set(
          prev.map((d) => `${d.playerCode}-${d.taskId}-${d.formId}`)
        );

        if (existingKeys.has(key)) return prev;

        return [...prev, data];
      });
    });
  };

  const isFinished = state.gameState?.status === 'Finished' || state.gameState?.status === 'PostGame';

  // Fetch state from API when game is NOT running
  useEffect(() => {
    if (isFinished) {
      let isCanceled = false;

      const fetchState = async () => {
        try {
          const fetchedState = await getGameState(gameCode);
          if (isCanceled) return;

          // Only update if we received a valid state object
          if (!fetchedState || !fetchedState.code) return;

          // Check if the state has changed
          const hasChanged = !isEqual(state.gameState, fetchedState);
          if (!hasChanged) return;

          setState((prev) => ({
            ...prev,
            gameState: fetchedState
          }));
        } catch (error) {
          console.error('Failed to fetch final game state:', error);
        }
      };

      fetchState();

      return () => {
        isCanceled = true;
      };
    }
  }, [gameCode, isFinished]);

  useEffect(() => {
    if (isFinished) {
      if (gameStateConnection && gameStateConnection.state === HubConnectionState.Connected) {
        gameStateConnection.stop();
        setGameStateConnection(null);
      }
      return;
    }

    if (!gameCode || gameStateConnection) return;
    let isCanceled = false;
    const userId =
      isFromFacilitator && auth.isAuthenticated
        ? auth.user?.profile.sub!
        : player?.playerState?.code;

    if (!userId) return;

    let gameStatePromise;
    if (isFromFacilitator && auth.isAuthenticated) {
      gameStatePromise = GetFacilitatorGameStateHub(gameCode);
    } else {
      gameStatePromise = GetGameStateHub(gameCode, userId);
    }

    gameStatePromise.then((resp) => {
      const { accessToken, endpointUrl, activityState } =
        resp as HubResponse & { activityState: activityState };

      if (accessToken && endpointUrl && activityState) {
        setState((prev) => {
          const content = { ...prev, activityState };
          putToStore('game-state', content);
          return content;
        });

        const hubConnectionSetup = new HubConnectionBuilder();
        hubConnectionSetup.withUrl(endpointUrl, {
          accessTokenFactory: () => accessToken
        });
        hubConnectionSetup.withAutomaticReconnect([0, 2000, 5000, 10000]);

        const hubConnection = hubConnectionSetup.build();
        GameConnectionListener(hubConnection);

        hubConnection
          .start()
          .then(() => {
            if (isCanceled) return hubConnection.stop();

            postBroadcastGameStateAsync(gameCode);
            setGameStateConnection(hubConnection);
          })
          .catch((error) => {
            if (isCanceled) return;
          });

        hubConnection.onreconnected(() => {
          if (isCanceled) return hubConnection.stop();

          postBroadcastGameStateAsync(gameCode);
          GameConnectionListener(hubConnection);
          setGameStateConnection(hubConnection);

          // hit AR mini games for get the result
          getARGameResult();
        });

        return () => {
          isCanceled = true;

          if (hubConnection.state === HubConnectionState.Connected)
            hubConnection.stop();

          setGameStateConnection(null);
        };
      }
    });
  }, [
    gameCode,
    isFromFacilitator,
    auth.isAuthenticated,
    auth.user?.profile.sub,
    player.playerState?.code,
    isFinished
  ]);

  useEffect(() => {
    // set initial game setting in runtime
    if (isFromFacilitator) {
      updateGameSettings(gameCode, runtimeSetting.runtime);
    }
  }, [isFromFacilitator, gameCode, runtimeSetting]);

  React.useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <GameContext.Provider
      value={[state, setState, gameStateConnection, isOnline]}>
      {props.children}
    </GameContext.Provider>
  );
};

interface activityState {
  hasGame: boolean;
  hasAssessment: boolean;
  hasFeedback: boolean;
  eventName: string;
}

export interface GameContextState {
  gameCode?: string;
  gameState?: GameState;
  activityState?: activityState;
  contentId?: string;
}

export const BuildNewGameState = (
  gameCode: string,
  runtime: { inGame: InGameSettings }
): GameState => {
  return {
    id: uuid(),
    name: '',
    code: gameCode,
    status: 'PreGame',
    pausedStatus: undefined,
    elapsedTime: [],
    isResume: undefined,
    startedUtc: undefined,
    finishedUtc: undefined,
    teams: [],
    players: [],
    runtime
  };
};

export { GameContext, GameStateProvider };
